import { Wrench, Rocket, Cog, Sparkles ,Car,LoaderPinwheel} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const services = [
  {
    title: "ECU Remapping",
    desc: "Dyno-tested remapping for optimized power, torque & throttle response. In collaboration with URA tuners.",
    icon: Wrench,
    accent: "from-amber-500 to-orange-500",
  },
  {
    title: "Bespoke Exhaust Systems",
    desc: "Performance exhaust upgrades in collaboration with Akrapovic & Capristo.",
    icon: Rocket,
    accent: "from-amber-500 to-yellow-500",
  },
{
  title: "Premium Wheel Upgrades",
  desc: "Custom wheel solutions ranging from 19-inch to 24-inch sizes, offered in collaboration with Vossen and HRE for unmatched style and performance.",
  icon: LoaderPinwheel,
  accent: "from-amber-500 to-red-500",
},
{
  title: "Body Kits",
  desc: "In collaboration with Larte Design, we offer premium body kits tailored for European vehicles, enhancing style, presence, and exclusivity.",
  icon: Car,
  accent: "from-amber-500 to-purple-500",
},
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
      duration: 0.6,
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function CoreServicesSection() {
  const sectionRef = useRef(null);
  const imageRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.1, 1]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.3, 0.8], [0.4, 1, 0.6]);

  return (
    <section ref={sectionRef} className="w-full bg-neutral-950 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 py-20">

        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.2 },
            },
          }}
          className="text-center mb-14"
        >
          <motion.h2
            variants={textVariants}
            className="text-3xl md:text-4xl font-bold relative inline-block"
          >
            Performance Upgrades
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-amber-500 to-transparent"
            />
          </motion.h2>

          <motion.p
            variants={textVariants}
            className="text-neutral-400 mt-4 max-w-3xl mx-auto"
          >
            Pick and Choose From The Best In The Industry.            </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch"
        >
          {services.map((service) => {
            const Icon = service.icon;

            return (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="group relative h-full"
              >
                {/* Glow */}
                <div
                  className={`absolute -inset-0.5 bg-gradient-to-r ${service.accent} rounded-2xl opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-50`}
                />

                {/* Card */}
                <motion.div
                  className="relative bg-neutral-900 border border-amber-500/20 rounded-2xl p-6 overflow-hidden flex flex-col justify-between h-full"
                  whileHover={{
                    y: -6,
                    transition: { type: "spring", stiffness: 300 },
                  }}
                >
                  {/* Top content */}
                  <div>
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-amber-500/10 mb-4">
                      <Icon className="w-6 h-6 text-amber-400" />
                    </div>

                    <h3 className="font-semibold text-lg mb-2">
                      {service.title}
                    </h3>

                    <p className="text-sm text-neutral-400 leading-relaxed">
                      {service.desc}
                    </p>
                  </div>

                  {/* Bottom line */}
                  <motion.div
                    className={`h-0.5 bg-gradient-to-r ${service.accent} mt-6`}
                    initial={{ width: 0 }}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>

      {/* Image Section */}
      <motion.div
        ref={imageRef}
        style={{
          scale: imageScale,
          opacity: imageOpacity,
        }}
        className="w-full h-[420px] overflow-hidden relative"
      >
        <motion.img
          src="/assets/perfomance/img1.jpg"
          alt="Performance upgrades"
          className="w-full h-full object-cover"
        />
      </motion.div>
    </section>
  );
}